import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Paper,
  Chip,
  Avatar,
  Divider,
  Fade,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Alert,
  AlertTitle,
  Backdrop,
  CircularProgress,
  Tooltip,
  Link,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Edit as EditIcon,
  CloudUpload as ExportIcon,
  CalendarToday as CalendarIcon,
  Link as LinkIcon,
  Info as InfoIcon,
  TrackChanges as TrackerIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  PlayArrow as ActiveIcon,
  StopCircle as StopIcon,
  PauseCircleFilled as StoppedIcon,
  YouTube as YouTubeIcon,
  MusicVideo as TikTokIcon,
  Instagram as InstagramIcon,
  CameraAlt as CameraIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Share as ShareIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useMutation, useQueryClient } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "sonner";

import useIsDesktop from "../../Hooks/useIsDesktop";
import { TrackerTable } from "./trackerTable";
import UploadFileDialog from "../../Components/tools/uploadfile";
import client from "../../API";
import routes from "../../Config/routes";
import TrackerProgressChart from "./TrackerProgressChart";
import { TrackerShareDialog } from "./shareTrackerDialog";

/* ────────────────────────────────────────────────────────────────── */
/* URL validation helper (mirrors newTracker)                        */
/* ────────────────────────────────────────────────────────────────── */

function isValidTrackerUrl(url) {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.replace("www.", "");
    return ["youtube.com", "youtu.be", "tiktok.com", "instagram.com"].includes(host);
  } catch {
    return false;
  }
}

/* ────────────────────────────────────────────────────────────────── */
/* Helpers                                                            */
/* ────────────────────────────────────────────────────────────────── */

function getStatusMeta(status) {
  switch (status?.toLowerCase()) {
    case "active":
      return { color: "success", icon: <ActiveIcon fontSize="small" />, label: "Active" };
    case "completed":
      return { color: "primary", icon: <CheckCircleIcon fontSize="small" />, label: "Completed" };
    case "archived":
      return { color: "default", icon: <ArchiveIcon fontSize="small" />, label: "Archived" };
    default:
      return { color: "default", icon: <TrackerIcon fontSize="small" />, label: status || "Unknown" };
  }
}

function detectPlatform(url = "") {
  const l = url.toLowerCase();
  if (l.includes("youtube.com") || l.includes("youtu.be")) return "youtube";
  if (l.includes("tiktok.com")) return "tiktok";
  if (l.includes("instagram.com")) return "instagram";
  return "link";
}

const PLATFORM_META = {
  youtube:   { label: "YouTube",   color: "#ff0000", Icon: YouTubeIcon   },
  tiktok:    { label: "TikTok",    color: "#010101", Icon: TikTokIcon    },
  instagram: { label: "Instagram", color: "#e1306c", Icon: InstagramIcon },
  link:      { label: "Link",      color: "#888",    Icon: LinkIcon      },
};

function extractUrl(fileOrUrl) {
  if (!fileOrUrl) return "";
  if (typeof fileOrUrl === "string") return fileOrUrl;
  return fileOrUrl.file_url || fileOrUrl.url || "";
}

/* ────────────────────────────────────────────────────────────────── */
/* StatBadge                                                          */
/* ────────────────────────────────────────────────────────────────── */

function StatBadge({ value, label, color = "primary" }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, borderRadius: 2.5, flex: 1, textAlign: "center",
        border: "1px solid",
        borderColor: alpha(theme.palette[color]?.main || theme.palette.divider, 0.2),
        bgcolor: alpha(theme.palette[color]?.main || theme.palette.primary.main, 0.04),
      }}
    >
      <Typography variant="h5" fontWeight={800} color={`${color}.main`}>{value}</Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
    </Paper>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* CoverPhoto                                                         */
/* ────────────────────────────────────────────────────────────────── */

function CoverPhoto({ photoUrl, onUploadClick, height = 240, editable = true, children }) {
  const [hovering, setHovering] = useState(false);
  return (
    <Box
      sx={{
        height, flexShrink: 0, position: "relative", overflow: "hidden",
        background: photoUrl
          ? `url('${photoUrl}') center/cover`
          : "linear-gradient(135deg,#0f2027 0%,#203a43 50%,#2c5364 100%)",
        cursor: editable ? "pointer" : "default",
      }}
      onMouseEnter={() => editable && setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={editable ? onUploadClick : undefined}
    >
      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(rgba(0,0,0,0.2),rgba(0,0,0,0.6))", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", inset: 0, opacity: 0.12, pointerEvents: "none", backgroundImage: "radial-gradient(circle at 20% 50%,white 1px,transparent 1px),radial-gradient(circle at 80% 20%,white 1px,transparent 1px)", backgroundSize: "40px 40px" }} />
      {editable && (
        <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, opacity: hovering ? 1 : 0, transition: "opacity 0.2s", backdropFilter: hovering ? "blur(3px)" : "none", pointerEvents: "none" }}>
          <Box sx={{ width: 50, height: 50, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.75)", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.1)" }}>
            <CameraIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Typography variant="caption" sx={{ color: "white", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", fontSize: "0.7rem" }}>
            {photoUrl ? "Change photo" : "Add cover photo"}
          </Typography>
        </Box>
      )}
      <Box sx={{ position: "relative", zIndex: 1, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* AddLinksPanel — inline link adder                                 */
/* ────────────────────────────────────────────────────────────────── */

function AddLinksPanel({ open, onSave, onClose, saving }) {
  const theme = useTheme();
  const [newLinks, setNewLinks] = useState([""]);

  useEffect(() => { if (!open) setNewLinks([""]); }, [open]);

  const validCount = newLinks.filter(l => isValidTrackerUrl(l)).length;

  return (
    <Collapse in={open} sx={{ flexShrink: 0 }}>
      <Box sx={{ px: { xs: 2, md: 4 }, pb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5, borderRadius: "14px",
            border: `1.5px solid ${alpha(theme.palette.primary.main, 0.22)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, mb: 1.5, color: "primary.main" }}>
            Add New Links
          </Typography>
          <Stack spacing={1}>
            {newLinks.map((link, i) => {
              const hasVal = !!link;
              const ok = isValidTrackerUrl(link) || !link;
              const plat = detectPlatform(link);
              const pm = PLATFORM_META[plat];
              return (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {/* Platform indicator */}
                  <Box sx={{
                    width: 32, height: 32, borderRadius: 1.5, flexShrink: 0,
                    background: hasVal && ok && plat !== "link"
                      ? `linear-gradient(135deg, ${pm.color}cc, ${pm.color}88)`
                      : alpha(theme.palette.divider, 0.3),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}>
                    <pm.Icon sx={{ fontSize: 15, color: hasVal && ok && plat !== "link" ? "white" : "text.disabled" }} />
                  </Box>
                  {/* Input */}
                  <Box sx={{
                    flex: 1, display: "flex", alignItems: "center", gap: 0.75,
                    px: 1.5, py: 0.9, borderRadius: 2.5, bgcolor: "background.paper",
                    border: `1.5px solid ${!ok && hasVal ? theme.palette.error.main : hasVal && ok ? theme.palette.success.main : alpha(theme.palette.divider, 0.7)}`,
                    transition: "border-color 0.2s",
                  }}>
                    <LinkIcon sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }} />
                    <input
                      value={link}
                      onChange={(e) => setNewLinks(prev => { const u = [...prev]; u[i] = e.target.value; return u; })}
                      placeholder="https://youtube.com/... or tiktok.com/... or instagram.com/..."
                      style={{
                        flex: 1, border: "none", outline: "none", background: "transparent",
                        fontSize: "0.82rem", fontFamily: "inherit",
                        color: !ok && hasVal ? "#d32f2f" : "inherit",
                      }}
                    />
                    {hasVal && ok && <CheckCircleIcon sx={{ fontSize: 14, color: "success.main", flexShrink: 0 }} />}
                    {hasVal && !ok && <CloseIcon sx={{ fontSize: 14, color: "error.main", flexShrink: 0 }} />}
                  </Box>
                  {newLinks.length > 1 && (
                    <IconButton size="small" onClick={() => setNewLinks(prev => prev.filter((_, idx) => idx !== i))}
                      sx={{ color: "text.disabled", "&:hover": { color: "error.main", bgcolor: alpha(theme.palette.error.main, 0.08) } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              );
            })}
          </Stack>

          <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setNewLinks(prev => [...prev, ""])}
              disabled={newLinks.length >= 10}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.75rem", borderRadius: 2 }}
            >
              Add row
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={onClose} sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={saving || validCount === 0}
              onClick={() => onSave(newLinks.filter(l => isValidTrackerUrl(l)))}
              startIcon={saving ? <CircularProgress size={12} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, fontSize: "0.75rem",
                borderRadius: 2, px: 2,
                background: "linear-gradient(135deg, #3b82f6, #a855f7)",
                "&.Mui-disabled": { opacity: 0.5 },
              }}
            >
              {saving ? "Saving…" : `Save ${validCount} link${validCount !== 1 ? "s" : ""}`}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Collapse>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Main                                                               */
/* ────────────────────────────────────────────────────────────────── */

export function Tracker({ open, handleClose, tracker, isOwner = true }) {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const queryClient = useQueryClient();

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [updatedTracker, setUpdatedTracker]     = useState(tracker);
  const [updating, setUpdating]                 = useState(false);
  const [exporting, setExporting]               = useState(false);
  const [stopConfirmOpen, setStopConfirmOpen]   = useState(false);
  const [stopping, setStopping]                 = useState(false);
  const [shareDialogOpen, setShareDialogOpen]   = useState(false);
  const [addLinkOpen, setAddLinkOpen]           = useState(false);
  const [savingLinks, setSavingLinks]           = useState(false);
  const [trackerHistory, setTrackerHistory]     = useState([]);

  /* ── Load history ──────────────────────────────────────────────── */
  useEffect(() => {
    if (!tracker?.id) return;
    client.trackers.getHistory(tracker.id)
      .then(data => setTrackerHistory(data || []))
      .catch(() => {});
  }, [tracker?.id]);

  useEffect(() => { setUpdatedTracker(tracker); }, [tracker]);

  /* ── Body scroll lock (desktop overlay) ────────────────────────── */
  useEffect(() => {
    if (!isDesktop || !open) return;
    const scrollY = window.scrollY;
    document.body.style.cssText = `overflow:hidden;position:fixed;top:-${scrollY}px;left:0;right:0;`;
    return () => {
      document.body.style.cssText = "";
      window.scrollTo(0, scrollY);
    };
  }, [open, isDesktop]);

  const isStopped = !!updatedTracker?.stop_tracking_at;
  const daysUntilHistoryDeleted = (() => {
    if (!updatedTracker?.delete_history_at) return null;
    const ms = new Date(updatedTracker.delete_history_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  const trackedBlitzCreators = updatedTracker?.links?.reduce((acc, l) => l.creatorId ? acc + 1 : acc, 0) ?? 0;
  const totalLinks = updatedTracker?.links?.length ?? 0;
  const statusMeta = getStatusMeta(updatedTracker?.status);

  const platformCounts = ["youtube", "tiktok", "instagram"].reduce((acc, p) => {
    acc[p] = (updatedTracker?.links || []).filter(l => detectPlatform(l?.link || "") === p).length;
    return acc;
  }, {});

  /* ── Mutations ─────────────────────────────────────────────────── */

  const { mutate: updateTracker } = useMutation(
    ({ id, data }) => client.trackers.update(id, data),
    {
      onSuccess: (_, variables) => {
        setUpdatedTracker(prev => ({ ...prev, ...variables.data }));
        setUpdating(false);
        toast.success("Cover photo updated!");
        queryClient.invalidateQueries("trackers");
      },
      onError: (err) => {
        toast.error("Update failed", { description: err?.response?.data?.error || err.message });
        setUpdating(false);
      },
    }
  );

  const { mutate: exportToSheets } = useMutation(
    (id) => client.trackers.exportSheet(id),
    {
      onSuccess: (data) => {
        setExporting(false);
        window.open(data.sheetLink, "_blank");
        toast.success("Exported to Google Sheets!");
      },
      onError: (err) => {
        toast.error("Export failed", { description: err?.response?.data?.error || err.message });
        setExporting(false);
      },
    }
  );

  const { mutate: stopTracker } = useMutation(
    (id) => client.trackers.stop(id),
    {
      onSuccess: (data) => {
        setStopping(false);
        setStopConfirmOpen(false);
        setUpdatedTracker(prev => ({ ...prev, stop_tracking_at: data.stop_tracking_at, delete_history_at: data.delete_history_at }));
        queryClient.invalidateQueries("trackers");
        toast.success("Tracking stopped", { description: "History will be deleted in 14 days." });
      },
      onError: (err) => {
        setStopping(false);
        toast.error("Failed to stop tracking", { description: err?.response?.data?.error || err.message });
      },
    }
  );

  /* ── Handlers ──────────────────────────────────────────────────── */

  const handleStopConfirm = () => { setStopping(true); stopTracker(updatedTracker.id); };

  const handleFileUploadSuccess = (fileOrUrl) => {
    const url = extractUrl(fileOrUrl);
    if (!url) { toast.error("Could not read the uploaded file URL. Please try again."); return; }
    setUploadDialogOpen(false);
    setUpdating(true);
    updateTracker({ id: updatedTracker.id, data: { tracker_photo: url } });
  };

  const handleExport = () => { setExporting(true); exportToSheets(updatedTracker.id); };

  const handleSaveNewLinks = useCallback(async (validLinks) => {
    if (!validLinks.length) return;
    setSavingLinks(true);
    try {
      const existing = updatedTracker?.links || [];
      const merged = [...existing, ...validLinks.map(link => ({ link, notes: "" }))];
      await client.trackers.update(updatedTracker.id, { links: merged });
      setUpdatedTracker(prev => ({ ...prev, links: merged }));
      toast.success(`${validLinks.length} link${validLinks.length !== 1 ? "s" : ""} added`);
      setAddLinkOpen(false);
      queryClient.invalidateQueries("trackers");
    } catch (err) {
      toast.error("Failed to save links", { description: err?.response?.data?.error || err.message });
    } finally {
      setSavingLinks(false);
    }
  }, [updatedTracker, queryClient]);

  if (!updatedTracker && open) return null;

  /* ══════════════════════════════════════════════════════════════════
     MOBILE
  ══════════════════════════════════════════════════════════════════ */

  if (!isDesktop) {
    return (
      <>
        <Dialog open={open} onClose={handleClose} fullScreen>
          {/* Fixed header — never scrolls */}
          <Box sx={{ flexShrink: 0, position: "sticky", top: 0, zIndex: 10 }}>
            <CoverPhoto
              photoUrl={updatedTracker?.tracker_photo}
              onUploadClick={() => isOwner && setUploadDialogOpen(true)}
              editable={isOwner}
              height={160}
            >
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                  <Typography variant="h5" fontWeight={700} color="white" sx={{ textShadow: "0 2px 4px rgba(0,0,0,0.5)", fontSize: "1.15rem", flex: 1, mr: 1 }} noWrap>
                    {updatedTracker?.name}
                  </Typography>
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); handleClose(); }}
                    sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", backdropFilter: "blur(6px)", flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap">
                  <Chip
                    icon={statusMeta.icon}
                    label={statusMeta.label}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600, backdropFilter: "blur(6px)" }}
                  />
                  <Chip
                    label={`${totalLinks} link${totalLinks !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "white", fontWeight: 600, backdropFilter: "blur(6px)" }}
                  />
                </Stack>
              </Box>
            </CoverPhoto>
          </Box>

          {/* Scrollable body */}
          <Box sx={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
            {/* Blitz creator alert */}
            {trackedBlitzCreators > 0 && (
              <Alert severity="info" sx={{ mx: 2, mt: 2, borderRadius: 2 }}>
                <AlertTitle fontWeight={700}>🚀 {trackedBlitzCreators} creator{trackedBlitzCreators > 1 ? "s" : ""} on Blitz</AlertTitle>
                <Link component={RouterLink} to={routes.search} fontWeight={600}>Launch Campaign →</Link>
              </Alert>
            )}

            <Box sx={{ p: 2 }}>
              {/* Stats */}
              <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                <StatBadge value={totalLinks} label="Links" color="info" />
                <StatBadge value={trackedBlitzCreators} label="On Blitz" color="success" />
              </Stack>

              {/* Stopped banner */}
              {isStopped && (
                <Alert severity="warning" icon={<StoppedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
                  <AlertTitle fontWeight={700}>Tracking stopped</AlertTitle>
                  {daysUntilHistoryDeleted !== null && (
                    <Typography variant="body2">History will be deleted in {daysUntilHistoryDeleted} day{daysUntilHistoryDeleted !== 1 ? "s" : ""}.</Typography>
                  )}
                </Alert>
              )}

              {/* Shared badge */}
              {updatedTracker?.is_shared && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Shared with you{updatedTracker._shared_by ? ` by ${updatedTracker._shared_by}` : ""} · {updatedTracker._permission === "view" ? "View only" : "Can edit"}
                  </Typography>
                </Alert>
              )}

              {/* Description */}
              {updatedTracker?.description && (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.divider, 0.05), border: "1px solid", borderColor: "divider", mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{updatedTracker.description}</Typography>
                </Paper>
              )}

              {/* Action buttons */}
              <Stack spacing={1} sx={{ mb: 2.5 }}>
                <Button
                  fullWidth variant="contained"
                  startIcon={<ExportIcon />}
                  onClick={handleExport}
                  sx={{ borderRadius: 2, fontWeight: 700, background: "linear-gradient(135deg,#11998e 0%,#38ef7d 100%)" }}
                >
                  Export to Sheets
                </Button>
                {isOwner && (
                  <Button
                    fullWidth variant="outlined"
                    startIcon={<ShareIcon />}
                    onClick={() => setShareDialogOpen(true)}
                    sx={{ borderRadius: 2, fontWeight: 600, borderColor: "#3b82f6", color: "#3b82f6" }}
                  >
                    Share Tracker
                  </Button>
                )}
                {isOwner && (
                  <Button
                    fullWidth variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setAddLinkOpen(p => !p)}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    {addLinkOpen ? "Cancel Adding Links" : "Add Links"}
                  </Button>
                )}
                {isOwner && (
                  <Button
                    fullWidth variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    Change Cover Photo
                  </Button>
                )}
                {isOwner && !isStopped && (
                  <Button
                    fullWidth variant="outlined" color="error"
                    startIcon={<StopIcon />}
                    onClick={() => setStopConfirmOpen(true)}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    Stop Tracking
                  </Button>
                )}
              </Stack>

              {/* Add links panel */}
              <AddLinksPanel
                open={addLinkOpen}
                onSave={handleSaveNewLinks}
                onClose={() => setAddLinkOpen(false)}
                saving={savingLinks}
              />

              {/* Chart */}
              {trackerHistory.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <TrackerProgressChart historyData={trackerHistory} />
                </Box>
              )}

              {/* Tracked content */}
              <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="subtitle2" fontWeight={700}>Tracked Content</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {["youtube", "tiktok", "instagram"].map((p) => {
                      const c = platformCounts[p];
                      if (!c) return null;
                      const m = PLATFORM_META[p];
                      return (
                        <Chip key={p}
                          icon={<m.Icon sx={{ fontSize: "11px !important", color: `${m.color} !important` }} />}
                          label={c} size="small"
                          sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: alpha(m.color, 0.08), color: m.color, border: `1px solid ${alpha(m.color, 0.2)}`, "& .MuiChip-label": { px: 0.75 } }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
                <TrackerTable tracker={updatedTracker} />
              </Paper>
            </Box>
          </Box>

          <Backdrop open={updating || exporting || stopping} sx={{ color: "#fff", zIndex: 9999, backdropFilter: "blur(4px)" }}>
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress color="inherit" sx={{ mb: 1.5 }} />
              <Typography variant="body2">{updating ? "Updating…" : exporting ? "Exporting…" : "Stopping…"}</Typography>
            </Box>
          </Backdrop>
        </Dialog>

        {/* Portaled dialogs — outside fullScreen dialog */}
        <UploadFileDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} onUploadSuccess={handleFileUploadSuccess} />
        <TrackerShareDialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} tracker={updatedTracker} isOwner={isOwner} />

        {/* Stop confirmation */}
        <Dialog open={stopConfirmOpen} onClose={() => !stopping && setStopConfirmOpen(false)} maxWidth="xs" fullWidth sx={{ zIndex: (t) => t.zIndex.modal + 3 }}>
          <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
            <StopIcon color="error" /> Stop tracking?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              We'll stop fetching new metrics right away. Existing history will stay visible for <strong>14 days</strong>, then it'll be deleted automatically.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setStopConfirmOpen(false)} disabled={stopping} sx={{ fontWeight: 600 }}>Cancel</Button>
            <Button onClick={handleStopConfirm} disabled={stopping} variant="contained" color="error"
              startIcon={stopping ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
              sx={{ fontWeight: 700, borderRadius: 2 }}>
              {stopping ? "Stopping…" : "Stop Tracking"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  /* ══════════════════════════════════════════════════════════════════
     DESKTOP full-screen overlay
  ══════════════════════════════════════════════════════════════════ */

  return (
    <>
      <Fade in={open} timeout={250}>
        <Box sx={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: "#f8f8f6",
          zIndex: theme.zIndex.modal + 1,
          display: open ? "flex" : "none",
          flexDirection: "column",
        }}>
          {/* ── Top bar ──────────────────────────────────────────── */}
          <Box sx={{
            height: 92, flexShrink: 0,
            borderBottom: "1px solid", borderColor: alpha(theme.palette.divider, 0.8),
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 3.5, bgcolor: "#fff", boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
          }}>
            {/* Left: avatar + title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                onClick={() => isOwner && setUploadDialogOpen(true)}
                sx={{
                  width: 54, height: 54, borderRadius: "50%", overflow: "hidden",
                  cursor: isOwner ? "pointer" : "default",
                  background: updatedTracker?.tracker_photo
                    ? `url('${updatedTracker.tracker_photo}') center/cover`
                    : "linear-gradient(135deg,#0f2027,#203a43)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                  position: "relative",
                  "&:hover .thumb-cam": { opacity: isOwner ? 1 : 0 },
                }}
              >
                {!updatedTracker?.tracker_photo && <TrackerIcon sx={{ color: "white", fontSize: 24 }} />}
                <Box className="thumb-cam" sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }}>
                  <CameraIcon sx={{ color: "white", fontSize: 18 }} />
                </Box>
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.25, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: "1.55rem", fontWeight: 800, lineHeight: 1.05 }}>{updatedTracker?.name}</Typography>
                  <Chip
                    icon={statusMeta.icon} label={statusMeta.label} color={statusMeta.color} size="small"
                    sx={{ fontWeight: 700, height: 30, borderRadius: 16, "& .MuiChip-label": { px: 1, fontSize: "0.92rem" } }}
                  />
                  {updatedTracker?.is_shared && (
                    <Chip
                      label={`Shared${updatedTracker._shared_by ? ` by ${updatedTracker._shared_by}` : ""}${updatedTracker._permission === "view" ? " · View only" : ""}`}
                      size="small"
                      sx={{ height: 26, fontSize: "0.7rem", fontWeight: 700, bgcolor: alpha("#3b82f6", 0.1), color: "#3b82f6", border: `1px solid ${alpha("#3b82f6", 0.25)}` }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                  {totalLinks} tracked link{totalLinks !== 1 ? "s" : ""} · created{" "}
                  {new Date(updatedTracker?.date_created || 0).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </Typography>
              </Box>
            </Box>

            {/* Right: actions */}
            <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={() => queryClient.invalidateQueries("trackers")} sx={{ width: 44, height: 44, bgcolor: "#f4f4f2", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                  <RefreshIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open public page" arrow>
                <IconButton
                  component="a" href={`/tracker/${updatedTracker?.id}`} target="_blank" rel="noopener noreferrer"
                  sx={{ width: 44, height: 44, bgcolor: "#f4f4f2", boxShadow: "0 1px 2px rgba(0,0,0,0.04)", "&:hover": { bgcolor: alpha(theme.palette.info.main, 0.15), color: "info.main" } }}
                >
                  <OpenInNewIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

              {isOwner && (
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => setShareDialogOpen(true)}
                  sx={{
                    height: 42, borderRadius: 999, px: 2.25, fontWeight: 700, fontSize: "0.8rem",
                    borderWidth: 1.5, borderColor: "#3b82f6", color: "#3b82f6",
                    "& .MuiButton-startIcon svg": { fontSize: 18 },
                    "&:hover": { borderColor: "#1d4ed8", background: alpha("#3b82f6", 0.06), color: "#1d4ed8" },
                  }}
                >
                  Share
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                sx={{
                  minWidth: 190, height: 42, borderRadius: 999, px: 2.5,
                  fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.01em",
                  background: "linear-gradient(135deg,#16c47f 0%,#24d98d 100%)",
                  boxShadow: "0 6px 16px rgba(36,217,141,0.22)",
                  "& .MuiButton-startIcon svg": { fontSize: 18 },
                  "&:hover": { background: "linear-gradient(135deg,#12b975 0%,#1fca82 100%)", boxShadow: "0 8px 20px rgba(36,217,141,0.28)" },
                }}
              >
                Export to Sheets
              </Button>

              {isOwner && !isStopped && (
                <Button
                  variant="outlined" color="error"
                  startIcon={<StopIcon />}
                  onClick={() => setStopConfirmOpen(true)}
                  sx={{ minWidth: 174, height: 42, borderRadius: 999, px: 2.25, fontWeight: 700, fontSize: "0.8rem", borderWidth: 1.5, "& .MuiButton-startIcon svg": { fontSize: 18 } }}
                >
                  Stop Tracking
                </Button>
              )}

              <IconButton onClick={handleClose} sx={{ width: 44, height: 44, bgcolor: "#f4f4f2", boxShadow: "0 1px 2px rgba(0,0,0,0.04)", "&:hover": { bgcolor: "action.selected" } }}>
                <CloseIcon sx={{ fontSize: 24 }} />
              </IconButton>
            </Box>
          </Box>

          {/* ── Body ─────────────────────────────────────────────── */}
          <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>

            {/* LEFT sidebar */}
            <Box sx={{ width: 360, flexShrink: 0, borderRight: 1, borderColor: "divider", bgcolor: "background.paper", display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <CoverPhoto
                photoUrl={updatedTracker?.tracker_photo}
                onUploadClick={() => isOwner && setUploadDialogOpen(true)}
                editable={isOwner}
                height={240}
              >
                <Box sx={{ p: 3 }}>
                  <Typography variant="h4" fontWeight={800} sx={{ color: "white", textShadow: "0 2px 8px rgba(0,0,0,0.6)", lineHeight: 1.2, mb: 1 }}>
                    {updatedTracker?.name}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip icon={statusMeta.icon} label={statusMeta.label} size="small" sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "white", fontWeight: 600, backdropFilter: "blur(8px)" }} />
                    <Chip
                      icon={<LinkIcon sx={{ fontSize: "14px !important", color: "white !important" }} />}
                      label={`${totalLinks} link${totalLinks !== 1 ? "s" : ""}`}
                      size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "white", fontWeight: 600, backdropFilter: "blur(8px)" }}
                    />
                  </Stack>
                </Box>
              </CoverPhoto>

              <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
                <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
                  <StatBadge value={totalLinks} label="Tracked" color="info" />
                  <StatBadge value={trackedBlitzCreators} label="On Blitz" color="success" />
                </Stack>

                {isStopped && (
                  <Alert severity="warning" icon={<StoppedIcon />} sx={{ mb: 3, borderRadius: 2.5, background: `linear-gradient(135deg, ${alpha("#ed6c02", 0.06)}, ${alpha("#ed6c02", 0.12)})`, border: `1px solid ${alpha("#ed6c02", 0.2)}` }}>
                    <AlertTitle sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Tracking stopped</AlertTitle>
                    <Typography variant="body2">
                      No new metrics will be fetched for this tracker.
                      {daysUntilHistoryDeleted !== null && <> History will be deleted in <strong>{daysUntilHistoryDeleted} day{daysUntilHistoryDeleted !== 1 ? "s" : ""}</strong>.</>}
                    </Typography>
                  </Alert>
                )}

                {trackedBlitzCreators > 0 && (
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2.5, background: `linear-gradient(135deg,${alpha("#1976d2", 0.06)},${alpha("#1976d2", 0.12)})`, border: `1px solid ${alpha("#1976d2", 0.2)}` }}>
                    <AlertTitle sx={{ fontWeight: 700, color: "info.main", fontSize: "0.9rem" }}>
                      🚀 {trackedBlitzCreators} creator{trackedBlitzCreators > 1 ? "s" : ""} on Blitz
                    </AlertTitle>
                    <Typography variant="body2" sx={{ mb: 1 }}>Unlock Demographic Projections, Instant Payments, and Daily Tracking.</Typography>
                    <Link component={RouterLink} to={routes.search} sx={{ fontWeight: 700, textDecoration: "none", color: "info.main", "&:hover": { textDecoration: "underline" } }}>
                      Launch Campaign →
                    </Link>
                  </Alert>
                )}

                {updatedTracker?.description && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <InfoIcon fontSize="small" color="primary" />
                      <Typography variant="subtitle2" fontWeight={700}>Description</Typography>
                    </Box>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, bgcolor: alpha(theme.palette.divider, 0.04), border: "1px solid", borderColor: alpha(theme.palette.divider, 0.6) }}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>{updatedTracker.description}</Typography>
                    </Paper>
                  </Box>
                )}

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <CalendarIcon fontSize="small" color="primary" />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.disabled" fontWeight={600}>CREATED</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {new Date(updatedTracker?.date_created || 0).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* RIGHT main content */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: "#fbfbfa" }}>

              {/* Right header */}
              <Box sx={{ px: 4.5, pt: 3.25, pb: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Box>
                  <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, mb: 0.25 }}>Tracked Content</Typography>
                  <Typography sx={{ fontSize: "0.95rem", color: "text.secondary" }}>
                    Performance and engagement across all monitored links
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  {["youtube", "tiktok", "instagram"].map((p) => {
                    const c = platformCounts[p];
                    if (!c) return null;
                    const m = PLATFORM_META[p];
                    return (
                      <Chip key={p}
                        icon={<m.Icon sx={{ fontSize: "12px !important", color: `${m.color} !important` }} />}
                        label={c} size="small"
                        sx={{ height: 36, fontSize: "0.78rem", fontWeight: 700, bgcolor: alpha(m.color, 0.08), color: m.color, border: `1px solid ${alpha(m.color, 0.2)}`, "& .MuiChip-label": { px: 0.9 }, "& .MuiChip-icon": { marginLeft: "10px", marginRight: "-4px" } }}
                      />
                    );
                  })}
                  {trackedBlitzCreators > 0 && (
                    <Chip icon={<ActiveIcon sx={{ fontSize: "15px !important" }} />} label={trackedBlitzCreators} variant="outlined"
                      sx={{ height: 36, borderRadius: 18, px: 0.75, fontWeight: 700, bgcolor: "#fff", borderColor: alpha(theme.palette.divider, 0.9) }}
                    />
                  )}
                  <Chip
                    icon={<LinkIcon sx={{ fontSize: "18px !important" }} />}
                    label={`${totalLinks} link${totalLinks !== 1 ? "s" : ""}`}
                    color="info" variant="outlined"
                    sx={{ height: 36, borderRadius: 18, px: 1, fontWeight: 700, bgcolor: "#fff", "& .MuiChip-icon": { marginLeft: "10px", marginRight: "-4px" } }}
                  />
                  {isOwner && (
                    <Tooltip title={addLinkOpen ? "Cancel" : "Add new links"} arrow>
                      <IconButton
                        onClick={() => setAddLinkOpen(p => !p)}
                        sx={{
                          width: 36, height: 36, borderRadius: 2,
                          bgcolor: addLinkOpen ? alpha(theme.palette.primary.main, 0.12) : "#fff",
                          border: `1.5px solid ${addLinkOpen ? theme.palette.primary.main : alpha(theme.palette.divider, 0.8)}`,
                          color: addLinkOpen ? "primary.main" : "text.secondary",
                          transition: "all 0.15s",
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), borderColor: "primary.main", color: "primary.main" },
                        }}
                      >
                        {addLinkOpen ? <CloseIcon sx={{ fontSize: 17 }} /> : <AddIcon sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Box>

              {/* Add links panel — outside scroll container */}
              <AddLinksPanel
                open={addLinkOpen}
                onSave={handleSaveNewLinks}
                onClose={() => setAddLinkOpen(false)}
                saving={savingLinks}
              />

              {/* Scrollable right content */}
              <Box sx={{ flex: 1, overflowY: "auto", px: 4, pb: 3 }}>
                <TrackerProgressChart historyData={trackerHistory} />
                <Paper
                  elevation={0}
                  sx={{ borderRadius: "18px", overflow: "hidden", border: "1px solid", borderColor: alpha(theme.palette.divider, 0.8), bgcolor: "#fff", boxShadow: "0 8px 18px rgba(0,0,0,0.045)" }}
                >
                  <TrackerTable tracker={updatedTracker} />
                </Paper>
              </Box>
            </Box>
          </Box>

          {/* Loading backdrop */}
          <Backdrop
            open={updating || exporting || stopping}
            sx={{ color: "#fff", zIndex: theme.zIndex.modal + 2, backdropFilter: "blur(8px)", bgcolor: "rgba(0,0,0,0.3)", position: "absolute" }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress color="inherit" size={56} thickness={4} sx={{ mb: 2 }} />
              <Typography variant="h6" fontWeight={700}>
                {updating ? "Updating cover photo…" : exporting ? "Exporting to Sheets…" : "Stopping tracker…"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                {updating ? "Saving your changes" : exporting ? "Preparing your data export" : "Pausing scraping for this tracker"}
              </Typography>
            </Box>
          </Backdrop>
        </Box>
      </Fade>

      {/* Portaled dialogs — above overlay (modal + 1 = 1301) */}
      <UploadFileDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} onUploadSuccess={handleFileUploadSuccess} />

      <TrackerShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        tracker={updatedTracker}
        isOwner={isOwner}
      />

      {/* Stop confirmation */}
      <Dialog open={stopConfirmOpen} onClose={() => !stopping && setStopConfirmOpen(false)} maxWidth="xs" fullWidth sx={{ zIndex: (t) => t.zIndex.modal + 3 }}>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <StopIcon color="error" /> Stop tracking this tracker?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            We'll stop fetching new metrics for this tracker right away. Existing history will stay visible for <strong>14 days</strong>, then it'll be deleted automatically.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStopConfirmOpen(false)} disabled={stopping} sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            onClick={handleStopConfirm} disabled={stopping}
            variant="contained" color="error"
            startIcon={stopping ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            {stopping ? "Stopping…" : "Stop Tracking"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}